import { useCallback, useMemo } from "react"

import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import {
  useGetBaseEquipmentForProposalQuery,
  type GetBaseEquipmentForProposalQuery,
} from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { isPowurLineProject } from "~/utils/powurline-utils"

import type { NewEquipment as EquipmentTypeInferredFromZod } from "../designs/design-model"

const coerceNumericId = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null
  }
  const numeric = Number(value)

  return Number.isFinite(numeric) ? numeric : null
}

export const normalizeBrandName = (value: unknown): string =>
  typeof value === `string` ? value.trim().toLowerCase() : ``

export type BaseEquipmentLookup = {
  isBaseModule: (id: unknown) => boolean
  isBaseInverter: (id: unknown) => boolean
  createBaseBrandMatcher: <T>(
    items: T[],
    getId: (item: T) => unknown,
    getBrand: (item: T) => unknown,
  ) => (brand: unknown) => boolean
  baseModuleId: number | null
  baseInverterIds: number[]
  isLoading: boolean
  isFetching: boolean
  refetch: ReturnType<typeof useGetBaseEquipmentForProposalQuery>[`refetch`]
}

// Wrapss base-equipment predicates so they short-circuit for non-PowurLine projects.
export const withPowurLineGuard = <Args extends unknown[]>(
  enabled: boolean,
  fn: (...args: Args) => boolean,
): ((...args: Args) => boolean) => {
  return (...args: Args) => (enabled ? fn(...args) : false)
}

// Builds a brand matcher backed by base-equipment IDs, gated to PowurLine projects.
export const createBaseBrandMatcherForItems = <T>(
  items: T[],
  getId: (item: T) => unknown,
  getBrand: (item: T) => unknown,
  {
    isPowurLine,
    isBaseInverter,
  }: {
    isPowurLine: boolean
    isBaseInverter: (id: unknown) => boolean
  },
): ((brand: unknown) => boolean) => {
  if (!isPowurLine) {
    return () => false
  }

  const baseBrands = new Set(
    items
      .filter((item) => isBaseInverter(getId(item)))
      .map((item) => normalizeBrandName(getBrand(item)))
      .filter(Boolean),
  )

  return (brand: unknown) => baseBrands.has(normalizeBrandName(brand))
}

const mapResponse = (
  data: GetBaseEquipmentForProposalQuery,
): { moduleId: number | null; inverterIds: number[] } => {
  const base = data.baseEquipmentForProposal

  return {
    moduleId: base?.moduleId ?? null,
    inverterIds: base?.inverters ?? [],
  }
}

export const useBaseEquipment = (): BaseEquipmentLookup => {
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const proposal = useProposalContext()

  const isPowurLine = isPowurLineProject(proposal.remote.enterpriseSettings)

  const query = useGetBaseEquipmentForProposalQuery(
    client,
    { proposalId: proposal.remote.id },
    {
      enabled: Boolean(proposal.remote.id) && isPowurLine,
      onError,
      select: mapResponse,
      staleTime: Infinity,
    },
  )

  const baseModuleNumericId = useMemo(
    () => (isPowurLine ? coerceNumericId(query.data?.moduleId ?? null) : null),
    [isPowurLine, query.data?.moduleId],
  )

  const baseInverterIdSet = useMemo(() => {
    if (!isPowurLine) {
      return new Set<number>()
    }

    const ids = query.data?.inverterIds ?? []

    return new Set(
      ids.map(coerceNumericId).filter((id): id is number => id !== null),
    )
  }, [isPowurLine, query.data?.inverterIds])

  const baseModuleChecker = useCallback(
    (id: unknown) => {
      const numeric = coerceNumericId(id)

      return numeric !== null && baseModuleNumericId !== null
        ? numeric === baseModuleNumericId
        : false
    },
    [baseModuleNumericId],
  )

  const baseInverterChecker = useCallback(
    (id: unknown) => {
      const numeric = coerceNumericId(id)

      return numeric !== null && baseInverterIdSet.has(numeric)
    },
    [baseInverterIdSet],
  )

  const isBaseModule = useMemo(
    () => withPowurLineGuard(isPowurLine, baseModuleChecker),
    [isPowurLine, baseModuleChecker],
  )

  const isBaseInverter = useMemo(
    () => withPowurLineGuard(isPowurLine, baseInverterChecker),
    [isPowurLine, baseInverterChecker],
  )

  const createBaseBrandMatcher = useCallback(
    <T>(
      items: T[],
      getId: (item: T) => unknown,
      getBrand: (item: T) => unknown,
    ) =>
      createBaseBrandMatcherForItems(items, getId, getBrand, {
        isPowurLine,
        isBaseInverter,
      }),
    [isPowurLine, isBaseInverter],
  )

  return {
    isBaseModule,
    isBaseInverter,
    createBaseBrandMatcher,
    baseModuleId: baseModuleNumericId,
    baseInverterIds: Array.from(baseInverterIdSet),
    isLoading: isPowurLine ? query.isLoading : false,
    isFetching: isPowurLine ? query.isFetching : false,
    refetch: query.refetch,
  }
}

export const createSiteEquipmentBaseBrandMatcher = (
  lookup: BaseEquipmentLookup,
  equipment: EquipmentTypeInferredFromZod[],
): ((brand: unknown) => boolean) =>
  lookup.createBaseBrandMatcher(
    equipment,
    (item) => item.id,
    (item) => item.manufacturer,
  )
